import { useSuspenseQuery } from '@tanstack/react-query';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Badge, Tooltip } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../state';

/**
 * Indicator when the comment is related to a review.
 */
export function CommentReviewPreview(props: {
    spaceId: string;
    changeRequestId: string;
    reviewId: string;
}) {
    const { spaceId, changeRequestId, reviewId } = props;

    const { data: review } = useSuspenseQuery(
        apiQuery(
            'getChangeRequestReviewById',
            {
                spaceId,
                changeRequestId,
                reviewId,
            },
            { nullOnError: true }
        )
    );

    if (!review) {
        return null;
    }

    const badge = (
        <Badge
            kind={
                review.outdated
                    ? 'secondary'
                    : review.status === api.ChangeRequestReviewStatus.Approved
                      ? 'success'
                      : 'danger'
            }
            size="xsmall"
            icon={
                review.status === api.ChangeRequestReviewStatus.Approved
                    ? Icon.ReviewApproved
                    : Icon.ReviewChangesRequested
            }
            label={getReviewBadgeLabel(review)}
            className="my-1.5"
        />
    );

    if (!review.outdated) {
        return badge;
    }

    return (
        <Tooltip label="This review is outdated, a more recent review has been requested or posted">
            {badge}
        </Tooltip>
    );
}

function getReviewBadgeLabel(review: api.ChangeRequestReview): string {
    switch (review.status) {
        case api.ChangeRequestReviewStatus.Approved:
            if (review.outdated) {
                return 'previously approved';
            }
            return 'reviewed and approved';
        case api.ChangeRequestReviewStatus.ChangesRequested:
            if (review.outdated) {
                return 'previously requested changes';
            }
            return 'reviewed and requested changes';
        default:
            assertNever(review.status);
    }
}
